/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
/* eslint-disable @typescript-eslint/no-unused-vars */
import { useContentStore } from "@/stores/content";
import { defaultPropertyStep } from "../hardCodedValues";
import ModuleTemplate from "./moduleTemplate";
import { Rectangle, Circle, Polygon, Path } from "../primitives";
import { ModuleProperty } from "../typeDefinations";
import { ChannelPort } from "../other";

function evalExpr(val: any, props: any): number {
  if (typeof val === 'number') return val;
  if (typeof val === 'string') {
    try {
      const func = new Function('p', 'return ' + val);
      return func(props);
    } catch (e) {
      console.warn("Failed to evaluate expr:", val);
      return 0;
    }
  }
  return 0;
}

export default class UserComponent extends ModuleTemplate {
  id = "";
  jsonData: any = null;

  // We start with empty or basic properties, will be overwritten by JSON
  properties: ModuleProperty = {
    height: {
      value: 400,
      name: "Height",
      step: defaultPropertyStep,
      unit: "µm"
    }
  };

  constructor(pos: number[], layer: string, jsonData?: any) {
    super(pos, layer);
    if (jsonData) {
      this.loadFromJson(jsonData);
    }
  }

  loadFromJson(data: any) {
    this.jsonData = JSON.parse(JSON.stringify(data));
    
    // Setup properties
    if (this.jsonData.properties) {
      this.properties = {} as ModuleProperty;
      for (const key in this.jsonData.properties) {
        const prop = this.jsonData.properties[key];
        this.properties[key] = {
          value: prop.value !== undefined ? prop.value : 0,
          name: prop.name || key,
          step: prop.step || defaultPropertyStep,
          unit: prop.unit !== undefined ? prop.unit : "µm"
        };
      }
      // Ensure height exists
      if (!this.properties.height) {
        this.properties.height = { value: 400, name: "Height", step: 10, unit: "µm" };
      }
    }
    this.updateRender();
  }

  clone(): UserComponent {
    const copy = new UserComponent(this.position, this.layer, this.jsonData);
    for (const key in this.properties) {
      if (copy.properties[key]) {
        copy.properties[key].value = this.properties[key].value;
      }
    }
    copy.setRotation(this.rotation);
    return copy;
  }

  setNewModuleId(): void {
    UserComponent.count++;
    const contentStore = useContentStore();
    while (contentStore.contentMap.has("UserComponent-" + UserComponent.count)) {
      UserComponent.count++;
    }
    this.id = "UserComponent-" + UserComponent.count;
  }

  get getModuleIndex(): number {
    return 7;
  }
  
  get numberOfChannelPorts(): number {
    return this.jsonData && this.jsonData.ports ? this.jsonData.ports.length : 0;
  }
  
  get portSizeFixed(): boolean {
    return false;
  }

  updatePortHelper(): void {
    if (!this.jsonData || !this.jsonData.ports) return;
    
    // Evaluate properties to 'p' object
    const p: any = {};
    for (const key in this.properties) p[key] = this.properties[key].value;

    const ports = this.jsonData.ports;
    
    // Adjust channelPorts array length if needed
    while (this.channelPorts.length < ports.length) {
      this.channelPorts.push(new ChannelPort(0, 0, this.id, this.channelPorts.length));
    }
    if (this.channelPorts.length > ports.length) {
      this.channelPorts.splice(ports.length);
    }

    for (let i = 0; i < ports.length; i++) {
      const portDef = ports[i];
      let px = 0, py = 0;

      // if port defines position array directly
      if (portDef.position && Array.isArray(portDef.position)) {
         px = evalExpr(portDef.position[0], p);
         py = evalExpr(portDef.position[1], p);
      }
      // if port references a node
      else if (portDef.node && this.jsonData.nodes && this.jsonData.nodes[portDef.node]) {
         const node = this.jsonData.nodes[portDef.node];
         px = evalExpr(node[0], p);
         py = evalExpr(node[1], p);
      }

      this.channelPorts[i].position[0] = this.position[0] + px;
      this.channelPorts[i].position[1] = this.position[1] + py;
    }
    
    if (this.rotation != 0) {
      const r = this.rotation;
      this.rotation = 0;
      this.setRotation(r);
    }
  }

  parseShape(shapeDef: any, p: any, isMask: boolean): any {
    const kind = shapeDef.kind;
    const fill = shapeDef.fill !== undefined ? shapeDef.fill : isMask;

    if (kind === "rect") {
      const x = evalExpr(shapeDef.xExpr || shapeDef.x, p);
      const y = evalExpr(shapeDef.yExpr || shapeDef.y, p);
      const w = evalExpr(shapeDef.widthExpr || shapeDef.width, p);
      const h = evalExpr(shapeDef.heightExpr || shapeDef.height, p);
      const rx = evalExpr(shapeDef.cornerRadiusExpr || shapeDef.cornerRadius || 0, p);
      return new Rectangle(this.position[0] + x, this.position[1] + y, rx, w, h, fill);
    } 
    else if (kind === "circle") {
      const cx = evalExpr(shapeDef.cxExpr || shapeDef.cx, p);
      const cy = evalExpr(shapeDef.cyExpr || shapeDef.cy, p);
      const r = evalExpr(shapeDef.rExpr || shapeDef.r, p);
      return new Circle(this.position[0] + cx, this.position[1] + cy, r, fill);
    }
    else if (kind === "polygon") {
      const pts = shapeDef.points.map((pt: any) => [
        this.position[0] + evalExpr(pt[0], p), 
        this.position[1] + evalExpr(pt[1], p)
      ]);
      return new Polygon(pts, fill, 0, 0);
    }
    else if (kind === "path") {
      const d = shapeDef.d; // Need to make this absolute or shifted based on this.position
      const sw = evalExpr(shapeDef.strokeWidthExpr || shapeDef.strokeWidth || 0, p);
      // Very basic translation support for paths, realistically path 'd' needs parsing or shifting
      return new Path(d, sw, fill, 0);
    }
    // Advanced shapes like tesla-path and serpentine-path
    else if (kind === "tesla-path") {
      const pairs = p[shapeDef.segPairNumProp];
      const lenX = p[shapeDef.segLenXProp];
      const lenY = p[shapeDef.segLenYProp];
      const width = p[shapeDef.channelWidthProp];
      const cx = evalExpr(shapeDef.cxExpr || shapeDef.cx || 0, p) + this.position[0];
      const cy = evalExpr(shapeDef.cyExpr || shapeDef.cy || 0, p) + this.position[1];
      
      const r = lenY / 2;
      let pathDef = `M${cx} ${cy}`;
      const rep = `l${lenX} ${-r} a${r} ${r} 0 0 1 0 ${lenY} m${-lenX} ${-r} l${lenX * 2} ${lenY} a${r} ${r} 0 0 0 0 ${-lenY} m${-lenX} ${r} l${lenX} ${-r}`;
      pathDef += rep.repeat(pairs);
      pathDef += `l${r} 0`;
      
      const path = new Path(pathDef, width, fill, 0, "TeslaValve");
      const pathMinX = cx;
      const pathMaxX = cx + (pairs * lenX * 2);
      const pathMinY = cy - lenY;
      const pathMaxY = cy + lenY;
      (path as any).bbox = [pathMinX, pathMinY, pathMaxX, pathMaxY];
      return path;
    }
    else if (kind === "serpentine-path") {
      const turningsNum = p[shapeDef.turningsNumProp];
      const overallWidth = p[shapeDef.widthProp];
      const channelDist = p[shapeDef.channelDistProp];
      const turnRadius = p[shapeDef.turningRadiusProp];
      const channelWidth = p[shapeDef.channelWidthProp];
      const cx = evalExpr(shapeDef.cxExpr || shapeDef.cx || 0, p) + this.position[0];
      const cy = evalExpr(shapeDef.cyExpr || shapeDef.cy || 0, p) + this.position[1];

      // Simplified serpentine generation logic based on DelayChannel
      const h = overallWidth - 2 * turnRadius;
      const hHalf = h / 2 - turnRadius;
      const dist = channelDist - turnRadius * 2;
      
      let pathDef = `M ${cx} ${cy}`;
      pathDef += turnRadius > 0 ? `v${-hHalf}` : `v${-h / 2}`;
      
      const rep = turnRadius > 0
          ? `a${turnRadius} ${turnRadius} 0 0 1 ${turnRadius} ${-turnRadius} h${dist} a${turnRadius} ${turnRadius} 0 0 1 ${turnRadius} ${turnRadius} v${h} a${turnRadius} ${turnRadius} 0 0 0 ${turnRadius} ${turnRadius} h${dist} a${turnRadius} ${turnRadius} 0 0 0 ${turnRadius} ${-turnRadius} v${-h}`
          : `h${dist} v${h} h${dist} v${-h}`;
      
      pathDef += rep.repeat(Math.floor((turningsNum - 1) / 2));
      
      pathDef += turnRadius > 0
          ? `a${turnRadius} ${turnRadius} 0 0 1 ${turnRadius} ${-turnRadius} h${dist} a${turnRadius} ${turnRadius} 0 0 1 ${turnRadius} ${turnRadius}`
          : `h${dist}`;
          
      if (turningsNum % 2) {
        pathDef += turnRadius > 0 ? `v${hHalf} a${turnRadius} ${turnRadius} 0 0 0 ${turnRadius} ${turnRadius}` : `v${h / 2}`;
      } else {
        pathDef += turnRadius > 0
            ? `v${h} a${turnRadius} ${turnRadius} 0 0 0 ${turnRadius} ${turnRadius} h${dist} a${turnRadius} ${turnRadius} 0 0 0 ${turnRadius} ${-turnRadius} v${-hHalf} a${turnRadius} ${turnRadius} 0 0 1 ${turnRadius} ${-turnRadius}`
            : `v${h} a${turnRadius} ${turnRadius} 0 0 0 ${turnRadius} ${turnRadius} h${dist} a${turnRadius} ${turnRadius} 0 0 0 ${turnRadius} ${-turnRadius} v${-h / 2}`;
      }
      
      const path = new Path(pathDef, channelWidth, fill, 0, "DelayChannel");
      const pathMinX = cx;
      const pathMaxX = cx + (turningsNum * channelDist);
      const pathMinY = cy - overallWidth / 2;
      const pathMaxY = cy + overallWidth / 2;
      (path as any).bbox = [pathMinX, pathMinY, pathMaxX, pathMaxY];
      return path;
    }
    // Advanced filter-grid generation
    else if (kind === "filter-grid") {
      const rows = p[shapeDef.rowsProp] || 3;
      const cols = p[shapeDef.colsProp] || 5;
      const radius = p[shapeDef.radiusProp] || 150;
      const w = p[shapeDef.widthProp] || 4000;
      const h = p[shapeDef.heightProp] || 2000;
      const cx = evalExpr(shapeDef.cxExpr || shapeDef.cx || 0, p) + this.position[0];
      const cy = evalExpr(shapeDef.cyExpr || shapeDef.cy || 0, p) + this.position[1];
      const shapeType = shapeDef.shape || "circle";

      const gridShapes = [];
      const gapX = (w - cols * 2 * radius) / (cols + 1);
      const stepX = gapX + 2 * radius;
      const startX = cx - w/2 + gapX + radius;

      const gapY = (h - rows * 2 * radius) / (rows + 1);
      const stepY = gapY + 2 * radius;
      const startY = cy - h/2 + gapY + radius;

      for (let c = 0; c < cols; c++) {
        for (let r = 0; r < rows; r++) {
          const px = startX + c * stepX;
          const py = startY + r * stepY;
          if (shapeType === "triangle") {
            const pt1 = [px, py - radius];
            const pt2 = [px - radius * 0.866, py + radius * 0.5];
            const pt3 = [px + radius * 0.866, py + radius * 0.5];
            gridShapes.push(new Polygon([pt1, pt2, pt3], fill, 0, 0));
          } else {
            gridShapes.push(new Circle(px, py, radius, fill));
          }
        }
      }
      return gridShapes;
    }
    
    return null;
  }

  updateRender(): void {
    if (!this.jsonData) {
      // Default placeholder if no JSON uploaded yet
      this.elementsToRender.borderShapes = [
        new Rectangle(this.position[0] - 500, this.position[1] - 500, 0, 1000, 1000, false)
      ];
      this.updatePort();
      return;
    }

    const p: any = {};
    for (const key in this.properties) {
      p[key] = this.properties[key].value;
    }

    const borderShapes: any[] = [];
    const maskShapes: any[] = [];

    if (this.jsonData.geometry) {
      if (this.jsonData.geometry.border) {
        this.jsonData.geometry.border.forEach((shapeDef: any) => {
          const shape = this.parseShape(shapeDef, p, false);
          if (Array.isArray(shape)) {
            borderShapes.push(...shape);
          } else if (shape) {
            borderShapes.push(shape);
          }
        });
      }
      if (this.jsonData.geometry.mask) {
        this.jsonData.geometry.mask.forEach((shapeDef: any) => {
          const shape = this.parseShape(shapeDef, p, true);
          if (Array.isArray(shape)) {
            maskShapes.push(...shape);
          } else if (shape) {
            maskShapes.push(shape);
          }
        });
      }
    }

    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;

    const expandBounds = (x: number, y: number) => {
      minX = Math.min(minX, x);
      maxX = Math.max(maxX, x);
      minY = Math.min(minY, y);
      maxY = Math.max(maxY, y);
    };

    [...borderShapes, ...maskShapes].forEach((s) => {
      if ((s as any).bbox) {
        const b = (s as any).bbox;
        expandBounds(b[0], b[1]);
        expandBounds(b[2], b[3]);
      } else if (s instanceof Rectangle) {
        expandBounds(s.x, s.y);
        expandBounds(s.x + s.width, s.y + s.height);
      } else if (s instanceof Circle) {
        expandBounds(s.cx - s.r, s.cy - s.r);
        expandBounds(s.cx + s.r, s.cy + s.r);
      } else if (s instanceof Polygon) {
        s.points.forEach((pt: number[]) => expandBounds(pt[0], pt[1]));
      }
    });

    if (this.channelPorts.length > 0) {
      this.channelPorts.forEach((port) => {
        expandBounds(port.position[0], port.position[1]);
      });
    }

    if (minX === Infinity) {
      minX = this.position[0]; minY = this.position[1];
      maxX = this.position[0]; maxY = this.position[1];
    }
    
    const margin = 2000;
    minX -= margin;
    minY -= margin;
    maxX += margin;
    maxY += margin;

    this.elementsToRender.topLeftCorner = [minX, minY];
    this.elementsToRender.centerPoint = [(minX + maxX) / 2, (minY + maxY) / 2];

    this.elementsToRender.borderShapes = borderShapes;
    this.elementsToRender.maskShapes = maskShapes;
    
    this.updatePort();
  }
}
